import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Linkedin, Instagram } from 'lucide-react';
import { BrandLogo } from '../Brand';

/**
 * D-082 — Footer Visual Replication
 * Matches the master reference: 5-column layout with gold section headers
 * (Brand · Platform · Trust · Company · Resources · Stay Connected).
 */

const PLATFORM_LINKS = [
  { to: '/',             label: 'Home',         testId: 'footer-platform-home' },
  { to: '/products',     label: 'Products',     testId: 'footer-platform-products' },
  { to: '/factories',    label: 'Factories',    testId: 'footer-platform-factories' },
  { to: '/intelligence', label: 'Intelligence', testId: 'footer-platform-intelligence' },
  { to: '/ai-search',    label: 'AI Search',    testId: 'footer-platform-ai-search' },
];

const TRUST_LINKS = [
  { to: '/trust',            label: 'Trust Centre',     testId: 'footer-trust-centre' },
  { to: '/trust/security',   label: 'Security',         testId: 'footer-trust-security' },
  { to: '/trust/compliance', label: 'Compliance',       testId: 'footer-trust-compliance' },
  { to: '/trust',            label: 'Verified Platform', testId: 'footer-trust-verified' },
];

const COMPANY_LINKS = [
  { to: '/why-giwahs', label: 'Why GIWAHS Exists', testId: 'footer-company-about' },
  { to: '/contact',    label: 'Careers',           testId: 'footer-company-careers' },
  { to: '/press',      label: 'Press',             testId: 'footer-company-press' },
  { to: '/contact',    label: 'Contact Us',        testId: 'footer-company-contact' },
];

const RESOURCES_LINKS = [
  { to: '/assistant',       label: 'GIWAHS Advisor', testId: 'footer-resources-advisor' },
  { to: '/sourcing-guides', label: 'Blog',           testId: 'footer-resources-blog' },
  { to: '/contact',         label: 'Help Center',    testId: 'footer-resources-help' },
  { to: '/sourcing-guides', label: 'Guides',         testId: 'footer-resources-guides' },
];

const Col = ({ heading, links, testId }) => (
  <div data-testid={testId}>
    <h3 className="font-mono-data text-xs tracking-[0.22em] uppercase text-gold">{heading}</h3>
    <ul className="mt-5 space-y-3 text-sm">
      {links.map((l, i) => (
        <li key={l.testId + i}>
          <Link
            to={l.to}
            data-testid={l.testId}
            className="giwahs-footer-link text-platinum-2"
          >
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => {
  return (
    <footer data-testid="giwahs-footer" className="w-full border-t border-hairline bg-[#050505] text-platinum">
      <div className="mx-auto w-full max-w-[1920px] px-5 sm:px-8 lg:px-16 xl:px-24 py-16 md:py-20 lg:py-24">
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-6 lg:gap-10">

          {/* Brand block (spans 2 cols on lg) */}
          <div className="lg:col-span-2" data-testid="footer-brand-block">
            <Link to="/" aria-label="GIWAHS — home" className="inline-flex items-center gap-3">
              <BrandLogo link={false} height={104} testId="footer-brand-logo" />
            </Link>
            <p className="mt-6 text-sm leading-relaxed text-platinum-2 max-w-[200px]">
              The Intelligent Platform<br />
              for Fashion Commerce
            </p>
            <div className="mt-6 flex items-center gap-3 font-mono-data text-[11px] tracking-[0.20em] uppercase text-gold">
              <span>Trust</span>
              <span style={{ color: 'rgba(200,169,107,0.45)' }}>·</span>
              <span>Connect</span>
              <span style={{ color: 'rgba(200,169,107,0.45)' }}>·</span>
              <span>Trade</span>
              <span style={{ color: 'rgba(200,169,107,0.45)' }}>·</span>
              <span>Grow</span>
            </div>
          </div>

          <Col heading="Platform"  links={PLATFORM_LINKS}  testId="footer-col-platform" />
          <Col heading="Trust"     links={TRUST_LINKS}     testId="footer-col-trust" />
          <Col heading="Company"   links={COMPANY_LINKS}   testId="footer-col-company" />
          <Col heading="Resources" links={RESOURCES_LINKS} testId="footer-col-resources" />
        </div>

        {/* Stay connected row */}
        <div className="mt-12 grid grid-cols-1 gap-10 border-t border-hairline pt-10 lg:grid-cols-6 lg:gap-10">
          <div className="lg:col-span-2" data-testid="footer-stay-connected">
            <h3 className="font-mono-data text-xs tracking-[0.22em] uppercase text-gold">Stay Connected</h3>
            <p className="mt-5 text-sm leading-relaxed text-platinum-2 max-w-[260px]">
              Get intelligence, updates, and insights straight to your inbox.
            </p>
            <form className="mt-5 flex max-w-[360px] items-center gap-0">
              <input
                type="email"
                placeholder="Enter your email"
                data-testid="footer-email-input"
                className="giwahs-footer-input flex-1 rounded-full px-4 py-2.5 text-sm placeholder:text-muted-warm focus:outline-none"
              />
              <button
                type="submit"
                data-testid="footer-email-submit"
                className="giwahs-social-pill -ml-10"
                aria-label="Subscribe"
              >
                <ArrowRight size={14} strokeWidth={1.6} />
              </button>
            </form>
            <div className="mt-6 flex items-center gap-3">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin"
                 className="giwahs-social-pill">
                <Linkedin size={14} strokeWidth={1.6} />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram"
                 className="giwahs-social-pill">
                <Instagram size={14} strokeWidth={1.6} />
              </a>
              <a href="https://x.com" target="_blank" rel="noopener noreferrer" data-testid="footer-x"
                 className="giwahs-social-pill">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Operational verification (kept — legally required surface) */}
          <div className="lg:col-span-4 grid grid-cols-1 gap-6" data-testid="footer-verification">
            <div>
              <h3 className="font-mono-data text-xs tracking-[0.22em] uppercase text-gold">Verified Entity</h3>
              <div className="mt-5 text-sm">
                <div className="font-medium text-platinum">Whaitiri Black Limited</div>
                <div className="mt-1 text-xs text-muted-warm">Trading as GIWAHS</div>
              </div>
              <dl className="mt-4 space-y-1.5 font-mono-data text-[11px] text-platinum-2">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <dt className="text-muted-warm">NZ Company No.</dt>
                  <dd className="text-platinum">6860633</dd>
                </div>
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <dt className="text-muted-warm">NZBN</dt>
                  <dd className="text-platinum">9429046803306</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom strip — matches reference */}
      <div className="w-full border-t border-hairline bg-[#050505]" data-testid="footer-bottom-bar">
        <div className="mx-auto w-full max-w-[1920px] px-5 sm:px-8 lg:px-16 xl:px-24 py-5">
          <div className="flex flex-col items-start justify-between gap-3 text-xs text-muted-warm md:flex-row md:items-center">
            <span data-testid="footer-copyright">© 2026 GIWAHS. All rights reserved.</span>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <Link to="/privacy" className="hover:text-gold transition-colors">Privacy Policy</Link>
              <Link to="/terms"   className="hover:text-gold transition-colors">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-gold transition-colors">Cookie Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
